/**
 * The "Preview — not recorded yet" card under the round form's tiles (Indra, 2026-10-04): what the hand being entered would score,
 * recalculated every time anything in the form changes, with nothing recorded until Record Round. The numbers come from
 * `previewWin` (game/preview.ts), the same engine calls Record Round makes, so the two cannot disagree.
 *
 * Folded by default to a compact summary — fan and what the winner gets on the first line, EVERY pattern name on the second, so a wrong
 * tile shows up at once (a pattern missing, or one nobody expected). Tap it to open the full card (tappable pattern chips, and how the
 * points work out); "Hide" folds it again. The HTML builder is pure and tested; `mountHandPreview` adds the behaviour.
 */

import { previewWin, type PreviewHand, type WinPreview } from '../game/preview';
import type { Game } from '../game/types';
import type { WinContext } from '../rules/types';
import { esc, formatDelta, formatPatternBreakdown } from './helpers';
import { formatSettlementExplanation } from './settlementExplain';

const DEFAULT_LABEL = 'Preview — not recorded yet';

export interface PreviewCardOptions {
  /** The small heading (default "Preview — not recorded yet"). */
  label?: string;
  /** Say how many tiles there are while the hand is short, instead of showing nothing (the calculator, where the card is always there). */
  explainWaiting?: boolean;
}

function winnerGain(preview: Extract<WinPreview, { kind: 'ok' }>): number {
  return preview.payments.find((p) => p.player === preview.round.winner)?.delta ?? 0;
}

function namesLine(preview: Extract<WinPreview, { kind: 'ok' }>): string {
  return preview.result.patterns.length > 0 ? preview.result.patterns.map((p) => p.label).join(' · ') : 'No scoring pattern';
}

function shell(inner: string): string {
  return `<div class="card hp-card">${inner}</div>`;
}

export function previewCardHtml(game: Game, preview: WinPreview, open: boolean, opts: PreviewCardOptions = {}): string {
  const LABEL = opts.label ?? DEFAULT_LABEL;
  switch (preview.kind) {
    case 'waiting':
      if (!opts.explainWaiting) return ''; // nothing to score before the hand is complete — and nothing covering the tiles meanwhile
      return shell(
        `<div class="hp-label">${LABEL}</div><p class="muted hp-plain">${
          preview.have === 0 ? 'Tap tiles below to build a hand.' : `<strong>${preview.have} of ${preview.need} tiles.</strong> Keep adding tiles — the result appears once the hand is complete.`
        }</p>`,
      );
    case 'pickWinningTile':
      return shell(`<div class="hp-label">${LABEL}</div><p class="muted hp-plain">Pick the winning tile above to see what this hand scores.</p>`);
    case 'problem':
      return shell(
        `<div class="hp-label">${LABEL}</div><span class="hp-status bad">This can't be recorded as it is</span>` +
          `<ul class="hp-problems">${preview.problems.map((p) => `<li>${esc(p)}</li>`).join('')}</ul>`,
      );
    case 'incomplete':
      return shell(`<div class="hp-label">${LABEL}</div><span class="hp-status bad">Not a winning hand</span><p class="muted hp-plain">${esc(preview.message)}</p>`);
    case 'below_minimum':
      return shell(`<div class="hp-label">${LABEL}</div><span class="hp-status bad">Not a valid win</span><p class="muted hp-plain">${esc(preview.message)}</p>`);
    case 'ok': {
      const gain = winnerGain(preview);
      const limit = preview.result.isLimit;
      const fan = preview.result.effectiveFan;
      if (!open) {
        return shell(
          `<button type="button" class="hp-line" data-hp="open" aria-label="Show the preview details">` +
            `<span class="hp-ico ${limit ? 'limit' : 'ok'}">${limit ? '★' : '✓'}</span>` +
            `<span class="hp-text"><strong>${fan} fan</strong> · winner ${esc(formatDelta(gain))}` +
            `<span class="hp-names">${esc(namesLine(preview))}</span></span><span class="hp-caret">▾</span></button>`,
        );
      }
      return shell(
        `<div class="hp-head"><span class="hp-label">${LABEL}</span>` +
          `<button type="button" class="hp-line hp-hide" data-hp="close" aria-label="Hide the preview details"><span class="hp-caret">▴ Hide</span></button></div>` +
          `<div class="hp-top"><div class="hp-fan">${fan}<small>fan${limit ? ' (limit hand)' : ''}</small></div>` +
          `<div class="hp-pts">winner ${esc(formatDelta(gain))}</div></div>` +
          `<span class="hp-status ${limit ? 'limit' : 'ok'}">${limit ? 'Limit hand — table maximum' : 'Valid win'}</span>` +
          formatPatternBreakdown(preview.result.patterns) +
          (preview.result.notes.length > 0 ? `<p class="muted hp-plain">${preview.result.notes.map(esc).join('<br />')}</p>` : '') +
          formatSettlementExplanation(game, preview.round),
      );
    }
  }
}

export interface HandPreviewInput {
  hand: PreviewHand;
  ctx: WinContext;
}

export interface HandPreviewSource {
  /**
   * What the form holds right now; undefined when there is nothing to preview (a drawn round is being entered); or `{ blocked }`
   * when the form itself cannot be scored yet (the discarder is the winner) — the words to show instead.
   */
  read(): HandPreviewInput | { blocked: string } | undefined;
}

export interface MountOptions extends PreviewCardOptions {
  /** Start with the full card open (the calculator) rather than folded. */
  startOpen?: boolean;
}

export function mountHandPreview(
  container: HTMLElement,
  initialGame: Game,
  source: HandPreviewSource,
  options: MountOptions = {},
): { refresh(): void; setGame(game: Game): void } {
  let open = options.startOpen ?? false;
  let game = initialGame;

  const refresh = (): void => {
    const input = source.read();
    if (!input) {
      container.innerHTML = '';
      return;
    }
    if ('blocked' in input) {
      container.innerHTML = shell(`<div class="hp-label">${options.label ?? DEFAULT_LABEL}</div><p class="muted hp-plain">${esc(input.blocked)}</p>`);
      return;
    }
    let preview: WinPreview;
    try {
      preview = previewWin(game, input.hand, input.ctx);
    } catch {
      container.innerHTML = ''; // a preview must never get in the way of recording a round
      return;
    }
    container.innerHTML = previewCardHtml(game, preview, open, options);
  };

  container.addEventListener('click', (e) => {
    const target = (e.target as HTMLElement).closest<HTMLElement>('[data-hp]');
    if (!target) return;
    open = target.dataset['hp'] === 'open';
    refresh();
  });

  refresh();
  return {
    refresh,
    setGame(next: Game): void {
      game = next;
      refresh();
    },
  };
}
